import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "common/components/ui/Drawer";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { IntegrationSettings } from "common/types";

import { INTEGRATIONS_MAP } from "../constants";
import useIntegrationMutations from "../datahooks/useIntegrationMutations";

export default function RemoveIntegrationDialog({
  type,
  isOpen,
  setIsOpen,
}: {
  type: keyof IntegrationSettings | null;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) {
  const isDesktop = useTwBreakpoint("lg");
  const { title } = type ? INTEGRATIONS_MAP[type] : { title: "" };

  const { removeIntegration, isRemovingIntegration } =
    useIntegrationMutations();

  function onRemove() {
    if (type) {
      removeIntegration(type).then(() => setIsOpen(false));
    }
  }

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Close = isDesktop ? DialogClose : DrawerClose;
  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;

  if (!type) return null;

  return (
    <Component open={isOpen} onOpenChange={setIsOpen}>
      <Content>
        <Title>Remove integration?</Title>
        <Description className="mb-8">
          Are you sure you want to remove{" "}
          <span className="text-sm-bold text-black-950">{title}</span>{" "}
          integration from the workspace?
        </Description>

        <div className="flex items-center justify-between gap-x-2.5">
          <Close asChild>
            <Button
              className="lg:flex-1"
              variant="secondary-black"
              disabled={isRemovingIntegration}
            >
              Cancel
            </Button>
          </Close>
          <Button
            className="lg:flex-1"
            variant="outline-danger"
            onClick={onRemove}
            isLoading={isRemovingIntegration}
          >
            Remove
          </Button>
        </div>
      </Content>
    </Component>
  );
}
